import axios from 'axios';
import { authService } from './authService';

/**
 * Serviço para enviar logs de erro do console para o backend
 * Implementa retry automático e falha silenciosa
 */
class ErrorLogService {
  constructor() {
    this.apiUrl = `${import.meta.env.VITE_API_URL || 'http://localhost:5001/api'}/console/errors`;
    this.maxRetries = 2;
    this.retryDelayMs = 100;
  }

  /**
   * Envia um erro para o backend
   * Falha silenciosamente se houver erro
   * @param {Error | string} error - Erro a ser logado
   * @param {string} pageUrl - URL da página (opcional)
   */
  async logError(error, pageUrl = null) {
    try {
      // Extrair mensagem e stack
      const message = typeof error === 'string' ? error : error?.message || String(error);
      const stack = typeof error === 'string' ? undefined : error?.stack;

      // Preparar payload
      const payload = {
        message: message || 'Unknown error',
        stack: stack,
        pageUrl: pageUrl || window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      };

      // Fazer request com retry
      await this.sendWithRetry(payload);
    } catch (err) {
      // Falha silenciosa - não loga para não criar loop infinito
      // console.debug('[ErrorLogService] Failed to log error:', err);
    }
  }

  /**
   * Envia a requisição com retry automático
   * @private
   */
  async sendWithRetry(payload, attempt = 0) {
    try {
      // Verificar se está autenticado antes de enviar
      const token = authService.getToken();
      if (!token) {
        // Usuário não autenticado - não envia
        return;
      }

      const response = await axios.post(this.apiUrl, payload, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        timeout: 5000, // 5 segundos de timeout
      });

      // Aceita 202 ou qualquer sucesso/erro do servidor
      // Não trata como erro mesmo se for rate limit (429)
      if (!response.ok && response.status !== 429) {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (err) {
      // Retry em caso de erro de rede ou timeout
      if (attempt < this.maxRetries) {
        await new Promise((resolve) =>
          setTimeout(resolve, this.retryDelayMs * (attempt + 1))
        );
        return this.sendWithRetry(payload, attempt + 1);
      }
      // Após retries, falha silenciosa
    }
  }
}

export const errorLogService = new ErrorLogService();
